import { Classes, Icon } from '@clarion-blueprint-shim';
import { ReactNode } from 'react';
import {
  Button,
  NumberField as RACNumberField,
  NumberFieldProps as RACNumberFieldProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import {
  ClarionDescription,
  ClarionFieldError,
  ClarionFieldGroup,
  ClarionInput,
  ClarionLabel,
} from './ClarionField';

export interface ClarionNumericInputProps extends Omit<RACNumberFieldProps, 'children'> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: any) => string);
  placeholder?: string;
  hideButtons?: boolean;
  stepSize?: number;
  majorStepSize?: number;
  minorStepSize?: number;
  onValueChange?: (valueAsNumber: number, valueAsString: string) => void;
  children?: ReactNode;
}

const containerStyles = tv({
  base: 'flex flex-col gap-1',
});

const groupStyles = tv({
  variants: {
    isFocusWithin: {
      false: 'border-gray-300 dark:border-gray-500',
      true: 'border-gray-600 dark:border-gray-300',
    },
    isInvalid: {
      true: 'border-red-600 dark:border-red-600',
    },
    isDisabled: {
      true: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800',
    },
  },
});

const inputStyles = tv({
  base: 'flex-1 min-w-0 outline-0 bg-transparent text-sm text-gray-800 dark:text-gray-200 disabled:text-gray-200 dark:disabled:text-gray-600',
  variants: {
    hideButtons: {
      false: 'mr-1',
    },
    isInvalid: {
      true: Classes.INTENT_DANGER,
    },
  },
});

const buttonStyles = tv({
  base: 'flex items-center justify-center w-8 h-full bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 hover:text-gray-700 dark:hover:text-gray-300',
});

export function ClarionNumericInput({
  label,
  description,
  errorMessage,
  placeholder,
  hideButtons = false,
  onValueChange,
  children,
  className,
  ...props
}: ClarionNumericInputProps) {
  return (
    <RACNumberField
      {...props}
      onChange={(value) => onValueChange?.(value, String(value))}
      className={composeRenderProps(className, (className) => containerStyles({ className }))}
    >
      {label && <ClarionLabel>{label}</ClarionLabel>}
      <ClarionFieldGroup
        className={composeRenderProps('', (_, renderProps) => groupStyles({ ...renderProps }))}
      >
        <ClarionInput
          placeholder={placeholder}
          className={inputStyles({ hideButtons, isInvalid: props.isInvalid })}
        />
        {!hideButtons && (
          <div className="flex flex-col">
            <Button slot="increment" className={buttonStyles()}>
              <Icon icon="chevron-up" />
            </Button>
            <Button slot="decrement" className={buttonStyles()}>
              <Icon icon="chevron-down" />
            </Button>
          </div>
        )}
      </ClarionFieldGroup>
      {description && <ClarionDescription>{description}</ClarionDescription>}
      <ClarionFieldError>{errorMessage}</ClarionFieldError>
      {children}
    </RACNumberField>
  );
}
